import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getArchivedResources, unarchiveResource, unarchivePlanningWeek, sendWeekBriefing } from '@/services/api';
import { 
  Archive, ArchiveRestore, FolderKanban, ClipboardCheck, 
  Zap, GitBranch, ClipboardList, Calendar, User, Columns3, CalendarDays,
  FileSpreadsheet, FileText, Mail, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Permanent delete (Master Admin only). Uses the same auth bearer as the API helper.
const deleteArchivedItem = async (type, id) => {
  const tok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const url = type === 'planning_week'
    ? `${API}/api/planning/archive-week/${id}`
    : `${API}/api/archived/${type}/${id}`;
  const r = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${tok}` } });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error(data.detail || `HTTP ${r.status}`);
  }
};

// Map an archived-item type to its export endpoint base URL.
// Returns `{ excelUrl, pdfUrl }` or null if exports aren't supported.
const exportUrls = (type, id) => {
  switch (type) {
    case 'project':
      return {
        excelUrl: `${API}/api/export/project/${id}?format=excel`,
        pdfUrl: `${API}/api/export/project/${id}/pdf`,
      };
    case 'audit':
      return {
        excelUrl: `${API}/api/export/audit/${id}?format=excel`,
        pdfUrl: `${API}/api/export/audit/${id}/pdf`,
      };
    case 'kaizen':
      return {
        excelUrl: `${API}/api/export/kaizen/${id}?format=excel`,
        pdfUrl: `${API}/api/export/kaizen/${id}?format=pdf`,
      };
    case 'vsm':
      return {
        excelUrl: `${API}/api/export/vsm/${id}?format=excel`,
        pdfUrl: `${API}/api/export/vsm/${id}?format=pdf`,
      };
    case 'checklist':
      // Built-in route only supports excel + docx — PDF intentionally omitted.
      return {
        excelUrl: `${API}/api/export/checklist/${id}?format=excel`,
        pdfUrl: null,
      };
    case 'custom_checklist':
      return {
        excelUrl: `${API}/api/custom-checklists/export/${id}/excel`,
        pdfUrl: `${API}/api/custom-checklists/export/${id}/pdf`,
      };
    case 'planning_week':
      return {
        excelUrl: `${API}/api/export/planning-archive/${id}?format=excel`,
        pdfUrl: `${API}/api/export/planning-archive/${id}?format=pdf`,
      };
    default:
      return null;
  }
};

// Authenticated download helper (the Resend/route exports are JWT-protected).
const downloadAuthed = async (url, filename) => {
  try {
    const tok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const r = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(`Export failed: ${e.message}`);
  }
};

export const ArchivePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMaster = user?.is_master_admin || user?.role === 'master_admin';
  const [archived, setArchived] = useState({
    projects: [],
    audits: [],
    kaizen_events: [],
    vsms: [],
    checklists: [],
    custom_checklists: [],
    planning_weeks: []
  });
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    loadArchived();
  }, []);

  const loadArchived = async () => {
    try {
      const data = await getArchivedResources();
      setArchived(data);
    } catch (error) {
      toast.error('Failed to load archived items');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (type, id) => {
    setRestoring(`${type}-${id}`);
    try {
      if (type === 'planning_week') {
        await unarchivePlanningWeek(id);
      } else {
        await unarchiveResource(type, id);
      }
      toast.success('Item restored successfully');
      loadArchived();
    } catch (error) {
      toast.error(error.message || 'Failed to restore item');
    } finally {
      setRestoring(null);
    }
  };

  const [deleting, setDeleting] = useState(null);
  const handleDeletePermanent = async (type, id, label) => {
    if (!isMaster) return;
    if (!window.confirm(`Permanently delete "${label || id}"?\n\nThis CANNOT be undone — the item will be removed from the database entirely.`)) return;
    setDeleting(`${type}-${id}`);
    try {
      await deleteArchivedItem(type, id);
      toast.success('Permanently deleted');
      loadArchived();
    } catch (error) {
      toast.error(error.message || 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const totalArchived = 
    archived.projects.length + 
    archived.audits.length + 
    archived.kaizen_events.length + 
    archived.vsms.length + 
    archived.checklists.length +
    (archived.custom_checklists?.length || 0) +
    (archived.planning_weeks?.length || 0);

  const ArchivedItem = ({ item, type, idField, titleField, icon: Icon }) => {
    const id = item[idField];
    const urls = exportUrls(type, id);
    const baseName = (item[titleField] || item.custom_name || `${type}_${id}`).replace(/[^a-z0-9_-]+/gi, '_').slice(0, 60);
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-gray-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold truncate">{item[titleField] || item.custom_name || 'Untitled'}</h3>
                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground flex-wrap">
                  <Badge variant="outline" className="capitalize">{item.industry || item.status || 'N/A'}</Badge>
                  {item.created_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-end shrink-0">
              {urls?.excelUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadAuthed(urls.excelUrl, `${baseName}.xlsx`)}
                  data-testid={`export-excel-${type}-${id}`}
                  title="Download Excel"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                </Button>
              )}
              {urls?.pdfUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadAuthed(urls.pdfUrl, `${baseName}.pdf`)}
                  data-testid={`export-pdf-${type}-${id}`}
                  title="Download PDF"
                >
                  <FileText className="h-4 w-4 mr-1" /> PDF
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleUnarchive(type, id)}
                disabled={restoring === `${type}-${id}`}
                data-testid={`restore-${type}-${id}`}
              >
                <ArchiveRestore className="h-4 w-4 mr-2" />
                {restoring === `${type}-${id}` ? 'Restoring...' : 'Restore'}
              </Button>
              {isMaster && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => handleDeletePermanent(type, id, item[titleField] || item.custom_name)}
                  disabled={deleting === `${type}-${id}`}
                  data-testid={`delete-permanent-${type}-${id}`}
                  title="Permanently delete (Master Admin only)"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleting === `${type}-${id}` ? '...' : 'Delete'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="archive-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
            <Archive className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Archive</h1>
            <p className="text-muted-foreground">
              {totalArchived} archived item{totalArchived !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <Tabs defaultValue="projects" className="space-y-4">
            <TabsList>
              <TabsTrigger value="projects" className="gap-2">
                <FolderKanban className="h-4 w-4" />
                Projects ({archived.projects.length})
              </TabsTrigger>
              <TabsTrigger value="audits" className="gap-2">
                <ClipboardCheck className="h-4 w-4" />
                Audits ({archived.audits.length})
              </TabsTrigger>
              <TabsTrigger value="kaizen" className="gap-2">
                <Zap className="h-4 w-4" />
                Kaizen ({archived.kaizen_events.length})
              </TabsTrigger>
              <TabsTrigger value="vsm" className="gap-2">
                <GitBranch className="h-4 w-4" />
                VSM ({archived.vsms.length})
              </TabsTrigger>
              <TabsTrigger value="checklists" className="gap-2">
                <ClipboardList className="h-4 w-4" />
                Checklists ({archived.checklists.length})
              </TabsTrigger>
              <TabsTrigger value="custom_checklists" className="gap-2" data-testid="tab-custom-checklists">
                <Columns3 className="h-4 w-4" />
                Custom Checklists ({archived.custom_checklists?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="planning_weeks" className="gap-2" data-testid="tab-planning-weeks">
                <CalendarDays className="h-4 w-4" />
                Planning Weeks ({archived.planning_weeks?.length || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="projects" className="space-y-3">
              {archived.projects.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No archived projects</p>
              ) : (
                archived.projects.map(item => (
                  <ArchivedItem 
                    key={item.project_id} 
                    item={item} 
                    type="project" 
                    idField="project_id" 
                    titleField="title"
                    icon={FolderKanban}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="audits" className="space-y-3">
              {archived.audits.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No archived audits</p>
              ) : (
                archived.audits.map(item => (
                  <ArchivedItem 
                    key={item.audit_id} 
                    item={item} 
                    type="audit" 
                    idField="audit_id" 
                    titleField="title"
                    icon={ClipboardCheck}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="kaizen" className="space-y-3">
              {archived.kaizen_events.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No archived Kaizen events</p>
              ) : (
                archived.kaizen_events.map(item => (
                  <ArchivedItem 
                    key={item.event_id} 
                    item={item} 
                    type="kaizen" 
                    idField="event_id" 
                    titleField="title"
                    icon={Zap}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="vsm" className="space-y-3">
              {archived.vsms.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No archived Value Stream Maps</p>
              ) : (
                archived.vsms.map(item => (
                  <ArchivedItem 
                    key={item.vsm_id} 
                    item={item} 
                    type="vsm" 
                    idField="vsm_id" 
                    titleField="title"
                    icon={GitBranch}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="checklists" className="space-y-3">
              {archived.checklists.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No archived checklists</p>
              ) : (
                archived.checklists.map(item => (
                  <ArchivedItem 
                    key={item.checklist_id} 
                    item={item} 
                    type="checklist" 
                    idField="checklist_id" 
                    titleField="name"
                    icon={ClipboardList}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="custom_checklists" className="space-y-3">
              {(archived.custom_checklists?.length || 0) === 0 ? (
                <p className="text-center text-muted-foreground py-8">No archived custom checklists</p>
              ) : (
                archived.custom_checklists.map(item => (
                  <ArchivedItem 
                    key={item.checklist_id} 
                    item={item} 
                    type="custom_checklist" 
                    idField="checklist_id" 
                    titleField="title"
                    icon={Columns3}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="planning_weeks" className="space-y-3">
              {(archived.planning_weeks?.length || 0) === 0 ? (
                <p className="text-center text-muted-foreground py-8">No archived planning weeks</p>
              ) : (
                archived.planning_weeks.map(item => {
                  const urls = exportUrls('planning_week', item.week_id);
                  return (
                    <Card key={item.archive_id} data-testid={`archived-week-${item.week_id}`} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                              <CalendarDays className="h-5 w-5 text-cyan-700" />
                            </div>
                            <div>
                              <h3 className="font-semibold">Week {item.week_id}</h3>
                              <p className="text-xs text-muted-foreground mt-1">
                                {item.monday} → {item.sunday} • {item.assignment_count} assignments
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Archived {item.archived_at ? new Date(item.archived_at).toLocaleString() : ''}{item.archived_by_name ? ` by ${item.archived_by_name}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-wrap justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadAuthed(urls.excelUrl, `planning_${item.week_id}.xlsx`)}
                              data-testid={`export-excel-planning_week-${item.week_id}`}
                            >
                              <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadAuthed(urls.pdfUrl, `planning_${item.week_id}.pdf`)}
                              data-testid={`export-pdf-planning_week-${item.week_id}`}
                            >
                              <FileText className="h-4 w-4 mr-1" /> PDF
                            </Button>
                            {isMaster && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const apiBase = process.env.REACT_APP_BACKEND_URL;
                                    const tok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
                                    const r = await fetch(`${apiBase}/api/planning/briefing/${item.week_id}/export`, {
                                      headers: { Authorization: `Bearer ${tok}` },
                                    });
                                    if (!r.ok) {
                                      const txt = await r.clone().text().catch(() => '');
                                      throw new Error(txt.slice(0, 200) || `HTTP ${r.status}`);
                                    }
                                    const blob = await r.blob();
                                    const filename = `briefing-${item.week_id}.xlsx`;
                                    const a = document.createElement('a');
                                    a.href = URL.createObjectURL(blob); a.download = filename; a.click();
                                    URL.revokeObjectURL(a.href);
                                    const subject = encodeURIComponent(`KAIZEN KANRI · Monday Morning Briefing — ${item.week_id}`);
                                    const body = encodeURIComponent(
                                      `Beste,\n\nIn de bijlage vind je de Monday Morning Briefing voor week ${item.week_id}.\n` +
                                      `Bestandsnaam: ${filename} (zojuist gedownload — sleep het bestand naar dit bericht als bijlage).\n\n` +
                                      `Met vriendelijke groet,\n\n— Gegenereerd vanuit kaizenkanri.nl`
                                    );
                                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                                    toast.success(`Briefing downloaded — attach ${filename} in the mail composer that just opened.`);
                                  } catch (e) {
                                    toast.error(e.message || 'Failed to generate briefing');
                                  }
                                }}
                                data-testid={`email-briefing-${item.week_id}`}
                                title="Download briefing & open mail composer (recipients added manually)"
                              >
                                <Mail className="h-4 w-4 mr-1" /> Email briefing
                              </Button>
                            )}
                            {isMaster && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                                onClick={() => handleDeletePermanent('planning_week', item.week_id, `Week ${item.week_id}`)}
                                disabled={deleting === `planning_week-${item.week_id}`}
                                data-testid={`delete-permanent-planning_week-${item.week_id}`}
                                title="Permanently delete this snapshot (Master Admin only)"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {deleting === `planning_week-${item.week_id}` ? '...' : 'Delete snapshot'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ArchivePage;
